// ============================================================================
//  MIS PETICIONES (F2 del sistema de peticiones, 11-oct-2026) — lo que el estudio le ha pedido a
//  Lealify —arreglos, mejoras, dudas— con número, estado y lo que se hizo. Antes solo vivía en el
//  WhatsApp de Carlos. Entrada PROPIA en el menú (no pestaña de Ayuda: Carlos pidió separarlas para
//  que se vean mejor). Aquí el estudio:
//    · ve sus peticiones en curso y las resueltas, con el historial;
//    · abre una nueva (tipo + frase + detalle + captura opcional) → nos llega por WhatsApp;
//    · contesta cuando le pedimos un dato («Esperando tu respuesta»; el menú lleva globo).
//  Comportamiento copiado de la agenda dental (agenda/src/components/ayuda/PeticionesTab.tsx), con
//  los tokens de marca de Let's Move. Backend: lealify-base/backend/routes/client-requests.js.
//  Plan: forobeta-ops/docs/lealify-peticiones-plan-20261010.md §4.
// ============================================================================
import { FormEvent, useCallback, useEffect, useState, type ElementType } from 'react';
import {
  ArrowLeft, Bug, CalendarClock, CheckCircle2, ExternalLink, HelpCircle, ImagePlus, Lightbulb,
  Loader2, Plus, Send, Ticket, X,
} from 'lucide-react';
import {
  ClientRequest, ClientRequestDetail, RequestKind, RequestStatus,
  CAPTURA_MAX_BYTES, CAPTURA_TIPOS,
  createClientRequest, getClientRequest, getClientRequestsCount, listClientRequests, replyClientRequest, subirCaptura,
} from '../../services/clientRequestsApi';

const STATUS_CLS: Record<RequestStatus, string> = {
  nueva: 'bg-surface-2 text-content-2',
  revision: 'bg-brand-blue/10 text-brand-blue',
  curso: 'bg-accent/15 text-accent-ink',
  esperando: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  hecha: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  descartada: 'bg-surface-2 text-content-3',
};

const TIPOS: { k: RequestKind; label: string; Icon: ElementType; ayuda: string }[] = [
  { k: 'bug', label: 'Algo no funciona', Icon: Bug, ayuda: 'Dinos qué pasó, en qué pantalla y con qué alumna, clase o cobro.' },
  { k: 'mejora', label: 'Quiero una mejora', Icon: Lightbulb, ayuda: 'Cuéntanos qué te ahorraría tiempo y cómo lo haces hoy.' },
  { k: 'duda', label: 'Tengo una duda', Icon: HelpCircle, ayuda: 'Si es de cómo usar algo, prueba primero en «Ayuda».' },
];

const CERRADAS: RequestStatus[] = ['hecha', 'descartada'];

function fecha(s: string | null | undefined, conHora = false): string {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(conHora ? { hour: 'numeric', minute: '2-digit' } : {}) });
}

/** Fecha SIN hora (eta_date es DATE): se toma el día AAAA-MM-DD tal cual, sin pasar por UTC — así
 *  «2026-10-20» no se vuelve «19 oct» según la zona horaria del navegador. */
function fechaDia(s: string | null | undefined): string {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return fecha(s);
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function Estado({ r }: { r: { status: RequestStatus; statusLabel: string } }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[r.status] || STATUS_CLS.nueva}`}>
      {r.statusLabel}
    </span>
  );
}

const inputCls = 'w-full rounded-lg border border-line bg-canvas px-3 py-2.5 text-sm text-content placeholder:text-content-3 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 disabled:opacity-60';
const btnPrimario = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3';
const avisoError = 'rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300';

// ── Formulario «Nueva petición» ─────────────────────────────────────────────
function Nueva({ onCancel, onCreated }: { onCancel: () => void; onCreated: (r: ClientRequest) => void }) {
  const [kind, setKind] = useState<RequestKind>('bug');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const elegirCaptura = (f: File | null) => {
    setError(null);
    if (!f) return;
    if (!CAPTURA_TIPOS.includes(f.type)) { setError('La captura debe ser una imagen JPG, PNG o WEBP.'); return; }
    if (f.size > CAPTURA_MAX_BYTES) { setError('La imagen pesa más de 5 MB.'); return; }
    setFile(f);
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    if (title.trim().length < 4) { setError('Escribe en una frase qué necesitas.'); return; }
    setEnviando(true);
    try {
      let attachmentUrl: string | null = null;
      if (file) attachmentUrl = await subirCaptura(file);
      const r = await createClientRequest({ title: title.trim(), detail: detail.trim() || undefined, kind, attachmentUrl });
      onCreated(r);
    } catch (err) {
      setError((err as Error).message || 'No se pudo enviar. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const tipo = TIPOS.find((t) => t.k === kind);
  return (
    <form onSubmit={enviar} className="rounded-2xl border border-line bg-surface p-4 shadow-sm" data-testid="peticion-form">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-content">Nueva petición</h3>
        <button type="button" onClick={onCancel} className="rounded-lg p-1.5 text-content-3 hover:bg-surface-2" aria-label="Cerrar"><X size={18} /></button>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {TIPOS.map(({ k, label, Icon }) => (
          <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${kind === k ? 'border-accent bg-accent/15 text-content' : 'border-line text-content-2 hover:bg-surface-2'}`}>
            <Icon size={16} className={kind === k ? 'text-accent-ink' : ''} /> {label}
          </button>
        ))}
      </div>

      <label className="mb-1 block text-xs font-semibold text-content-2" htmlFor="pet-titulo">¿Qué necesitas? (una frase)</label>
      <input id="pet-titulo" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} disabled={enviando}
        placeholder={kind === 'bug' ? 'Ej. No me deja cobrar la mensualidad de una alumna' : kind === 'mejora' ? 'Ej. Que el recibo le llegue a la alumna por WhatsApp' : 'Ej. ¿Cómo doy de baja a una alumna sin borrar sus pagos?'}
        className={`mb-3 ${inputCls}`} />

      <label className="mb-1 block text-xs font-semibold text-content-2" htmlFor="pet-detalle">Detalle <span className="font-normal text-content-3">(opcional)</span></label>
      <textarea id="pet-detalle" value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={4000} rows={4} disabled={enviando}
        placeholder={tipo?.ayuda}
        className={`mb-3 resize-y ${inputCls}`} />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2 text-sm text-content-2 hover:bg-surface-2">
          <ImagePlus size={16} /> {file ? 'Cambiar captura' : 'Adjuntar captura (opcional)'}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={enviando} data-testid="peticion-captura"
            onChange={(e) => { elegirCaptura(e.target.files?.[0] || null); e.target.value = ''; }} />
        </label>
        {preview && (
          <div className="relative">
            <img src={preview} alt="Captura adjunta" className="h-16 w-auto rounded-lg border border-line object-cover" />
            <button type="button" onClick={() => setFile(null)} className="absolute -right-2 -top-2 rounded-full bg-surface p-0.5 text-content-2 shadow ring-1 ring-line" aria-label="Quitar captura"><X size={14} /></button>
          </div>
        )}
      </div>

      {error && <p className={`mb-3 ${avisoError}`}>{error}</p>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-content-3">Nos llega al momento. Te contestamos aquí y, si hace falta, por WhatsApp.</p>
        <button type="submit" disabled={enviando || title.trim().length < 4} className={`${btnPrimario} py-2.5`} data-testid="peticion-enviar">
          {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Enviar petición
        </button>
      </div>
    </form>
  );
}

// ── Detalle con historial y respuesta ───────────────────────────────────────
function Detalle({ id, onBack, onChanged }: { id: number; onBack: () => void; onChanged: () => void }) {
  const [r, setR] = useState<ClientRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(() => {
    getClientRequest(id).then(setR).catch((e) => setError((e as Error).message));
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const responder = async (e: FormEvent) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true); setError(null);
    try {
      await replyClientRequest(id, texto.trim());
      setTexto('');
      cargar();
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  const volver = (
    <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-content-3 hover:text-content">
      <ArrowLeft size={16} /> Mis peticiones
    </button>
  );
  if (error && !r) return <div>{volver}<p className={avisoError}>{error}</p></div>;
  if (!r) return <div className="flex justify-center py-10 text-content-3"><Loader2 className="animate-spin" /></div>;
  const abierta = !CERRADAS.includes(r.status);

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm sm:p-5" data-testid="peticion-detalle">
      {volver}
      <div className="flex flex-col-reverse items-start gap-1.5 sm:flex-row sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="text-xs text-content-3">#{r.id} · {r.kindLabel} · {fecha(r.requested_at || r.created_at)}{r.requested_by ? ` · ${r.requested_by}` : ''}</p>
          <h3 className="mt-0.5 font-display text-lg font-semibold text-content">{r.title}</h3>
        </div>
        <Estado r={r} />
      </div>

      {r.status === 'esperando' && (
        <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">Te pedimos un dato para poder seguir. Contéstanos abajo.</p>
      )}
      {r.eta_date && abierta && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-content-2"><CalendarClock size={15} /> Lo tenemos para el {fechaDia(r.eta_date)}.</p>
      )}
      {r.detail && <p className="mt-3 whitespace-pre-wrap text-sm text-content-2">{r.detail}</p>}
      {r.attachment_url && (
        <a href={r.attachment_url} target="_blank" rel="noreferrer" className="mt-3 block w-fit">
          <img src={r.attachment_url} alt="Captura" className="max-h-48 rounded-lg border border-line" />
        </a>
      )}
      {r.resolution && (
        // En «esperando», resolution = lo que le pedimos al cliente (11-oct-2026).
        <div className={`mt-4 rounded-xl p-3 ${r.status === 'esperando' ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
          <p className={`flex items-center gap-1.5 text-sm font-semibold ${r.status === 'esperando' ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}><CheckCircle2 size={16} /> {r.status === 'esperando' ? 'Lo que te pedimos' : r.status === 'descartada' ? 'Por qué no por ahora' : 'Lo que hicimos'}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-content">{r.resolution}</p>
        </div>
      )}
      {r.doc_url && (
        <a href={r.doc_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-blue hover:underline">
          <ExternalLink size={15} /> Ver la guía
        </a>
      )}

      {r.events.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-3">Historial</p>
          <ol className="space-y-2 border-l border-line pl-4">
            {r.events.map((e, i) => (
              <li key={i} className="relative">
                <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${e.actor === 'cliente' ? 'bg-accent-solid ring-1 ring-on-accent/20' : 'bg-brand-blue'}`} />
                <p className="text-xs text-content-3">{fecha(e.at, true)} · {e.actor === 'cliente' ? 'Tu estudio' : 'Lealify'}{e.statusLabel ? ` · ${e.statusLabel}` : ''}</p>
                {e.note && <p className="whitespace-pre-wrap text-sm text-content">{e.note}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {abierta && (
        <form onSubmit={responder} className="mt-5 flex items-end gap-2" data-testid="peticion-responder">
          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} maxLength={4000} disabled={enviando}
            placeholder={r.status === 'esperando' ? 'Escribe el dato que te pedimos…' : 'Agrega un comentario o un dato…'}
            className={`flex-1 resize-y ${inputCls}`} />
          <button type="submit" disabled={enviando || !texto.trim()} aria-label="Enviar respuesta"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-solid text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
            {enviando ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      )}
      {error && r && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>}
    </div>
  );
}

// ── Página ──────────────────────────────────────────────────────────────────
function PeticionesInner() {
  const [filtro, setFiltro] = useState<'abiertas' | 'hechas'>('abiertas');
  const [lista, setLista] = useState<ClientRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    listClientRequests(filtro).then(setLista).catch((e) => setError((e as Error).message));
  }, [filtro]);
  useEffect(() => { setLista(null); cargar(); }, [cargar]);

  return (
    <div className="w-full" data-testid="peticiones-page">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <Ticket className="text-brand-blue" size={24} /> Mis peticiones
        </h2>
        <p className="mt-1 text-sm text-content-3">Lo que nos has pedido —arreglos, mejoras, dudas—, en qué va y qué se hizo.</p>
      </div>

      {abierta !== null ? (
        <Detalle id={abierta} onBack={() => { setAbierta(null); cargar(); }} onChanged={cargar} />
      ) : (
        <div className="flex w-full flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-xl bg-surface-2 p-1">
              {(['abiertas', 'hechas'] as const).map((f) => (
                <button key={f} type="button" onClick={() => setFiltro(f)} aria-pressed={filtro === f}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${filtro === f ? 'bg-surface text-content shadow-sm' : 'text-content-3 hover:text-content'}`}>
                  {f === 'abiertas' ? 'En curso' : 'Resueltas'}
                </button>
              ))}
            </div>
            {!nueva && (
              <button type="button" onClick={() => { setNueva(true); setAviso(null); }} className={btnPrimario} data-testid="peticion-nueva">
                <Plus size={16} /> Nueva petición
              </button>
            )}
          </div>

          {aviso && <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">{aviso}</p>}

          {nueva && (
            <Nueva onCancel={() => setNueva(false)}
              onCreated={(r) => { setNueva(false); setAviso(`Listo, recibimos tu petición #${r.id}. Te contestamos aquí.`); if (filtro === 'abiertas') cargar(); else setFiltro('abiertas'); }} />
          )}

          {error && <p className={avisoError}>{error}</p>}
          {lista === null && !error && <div className="flex justify-center py-10 text-content-3"><Loader2 className="animate-spin" /></div>}

          {lista && lista.length === 0 && (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-line py-10 text-center text-content-3">
              <Ticket size={30} className="mb-2 opacity-50" />
              <p className="text-sm">{filtro === 'abiertas' ? 'No tienes peticiones en curso.' : 'Todavía no hay peticiones resueltas.'}</p>
              {filtro === 'abiertas' && <p className="mt-1 max-w-sm text-xs">Si algo no funciona o quieres una mejora, toca «Nueva petición». Lo que nos pidas por WhatsApp también aparece aquí.</p>}
            </div>
          )}

          {lista && lista.length > 0 && (
            <ul className="space-y-2" data-testid="peticiones-lista">
              {lista.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => setAbierta(r.id)}
                    className={`flex w-full flex-col-reverse items-start gap-1.5 rounded-2xl border bg-surface p-3 text-left shadow-sm transition hover:border-brand-blue/40 sm:flex-row sm:items-center sm:gap-3 ${r.status === 'esperando' ? 'border-amber-500/60' : 'border-line'}`}>
                    {/* Móvil: la etiqueta de estado va ARRIBA y el título a todo lo ancho (en 390 px el
                        título se cortaba); desde sm vuelve a la derecha. */}
                    <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                      <p className="line-clamp-2 text-sm font-semibold text-content sm:line-clamp-1">{r.title}</p>
                      <p className="mt-0.5 text-xs text-content-3">
                        #{r.id} · {r.kindLabel} · {fecha(r.requested_at || r.created_at)}
                        {r.eta_date && !CERRADAS.includes(r.status) ? ` · para el ${fechaDia(r.eta_date)}` : ''}
                        {r.resolved_at && CERRADAS.includes(r.status) ? ` · cerrada el ${fecha(r.resolved_at)}` : ''}
                      </p>
                    </div>
                    <Estado r={r} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Quién la usa lo decide el servidor (Carlos 11-oct-2026); quien no puede y llega por el enlace ve a quién pedírselo.
export default function PeticionesPage() {
  const [puede, setPuede] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    getClientRequestsCount().then((c) => { if (alive) setPuede(c.puede !== false); }).catch(() => { if (alive) setPuede(true); });
    return () => { alive = false; };
  }, []);
  if (puede === null) return null;
  if (puede) return <PeticionesInner />;
  return (
    <div className="w-full p-4" data-testid="peticiones-sin-permiso">
      <h1 className="mb-3 text-xl font-semibold text-content">Mis peticiones</h1>
      <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2">
        Las peticiones a Lealify las lleva la dueña o gerente del estudio. Si necesitas algo, pídeselo a ella.
      </p>
    </div>
  );
}
